import * as Sentry from '@sentry/angular';
import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from './app/app.config';
import { App } from './app/app';
import { environment } from './environments/environment';

/**
 * Sentry bootstrap. Runs BEFORE the Angular app so failures during bootstrap are captured too.
 *
 * Sentry is OPTIONAL and OFF by default: with an empty `sentryDsn` (the local-dev default) init is
 * skipped entirely — nothing is sent, zero overhead. Mirrors the backend's SENTRY_DSN gate and
 * keeps noisy local errors out of the shared quota. Set the DSN only on deployed environments.
 */
if (environment.sentryDsn) {
  Sentry.init({
    dsn: environment.sentryDsn,
    environment: environment.production ? 'production' : 'development',
    release: environment.appVersion,
    // Errors only. Raise per-environment if page-load / route timing is ever wanted, to bound cost.
    tracesSampleRate: 0,
    // This is a crypto-tax app: NEVER let the SDK auto-collect anything that could carry a card
    // number, wallet address, tax figure or token. We attach only a bare user id ourselves (see
    // AuthService). Session Replay is deliberately NOT enabled — it would record the portfolio
    // screens and the card form.
    dataCollection: {
      userInfo: false, // no email / ip / username
      cookies: false, // session + CSRF cookies
      httpHeaders: { request: false, response: false }, // Authorization, Cookie, …
      httpBodies: [], // no request/response bodies at all
      urlQueryParams: false, // tokens are sometimes passed in the query string
    },
    // Browser noise that is not our bug: extension scripts, a benign layout warning, and
    // requests aborted because the user navigated away mid-flight.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      'AbortError',
    ],
    denyUrls: [/^chrome:\/\//i, /^chrome-extension:\/\//i, /^moz-extension:\/\//i, /^safari-extension:\/\//i],
    beforeSend: scrubSensitiveData,
  });
}

/**
 * Last line of defence against leaking PII to Sentry. Drops request-level data the SDK may have
 * collected and reduces `user` to a bare id — card data, wallet addresses, tokens and tax figures
 * must never end up in an error report.
 */
function scrubSensitiveData(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  if (event.request) {
    delete event.request.cookies;
    delete event.request.data;
    delete event.request.query_string;
    delete event.request.headers;
  }

  // Keep ONLY the user id — never email, ip or username.
  if (event.user) {
    event.user = event.user.id ? { id: String(event.user.id) } : {};
  }

  return event;
}

bootstrapApplication(App, appConfig).catch((err) => {
  // A bootstrap failure means the app never rendered — the most severe error we can have, and the
  // one Sentry would otherwise never see: catching the rejection here stops it reaching any
  // unhandled-rejection hook, and the ErrorHandler that would normally report it is part of the
  // application that just failed to start. Report it explicitly.
  // captureException is a safe no-op when Sentry was not initialised (no DSN).
  Sentry.captureException(err, { level: 'fatal', tags: { phase: 'bootstrap' } });
  console.error(err);
});
